import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import {
  CalendarDays,
  ChevronRight,
  Eye,
  Gem,
  Search,
  X,
} from "lucide-react";
import { AppDispatch, RootState } from "../../../../store";
import { getPearlsCommission } from "../../../../Redux/referral/slice";
import Pagenation from "../../../../ecommerce/globalcomponents/pagenation/Pagenation";
import DateRangePicker from "../../../../ecommerce/globalcomponents/DateRangePicker";
import {
  formatEarningsCurrency,
  formatEarningsDate,
  formatEarningsLongDate,
  normalizeEarningsStatus,
  statusLabel,
} from "../earningsAdapters";
import "./SalarPearls.css";

const statusClass = (status: string) =>
  `salar-pearls-status salar-pearls-status-${normalizeEarningsStatus(status)}`;

const SalarCoins = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { pearlsCommission } = useSelector(
    (state: RootState) => state.referral,
  );

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [dateRange, setDateRange] = useState({ from: "", to: "" });

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    if (dateRange.from && !dateRange.to) return;

    dispatch(
      getPearlsCommission({
        page,
        limit,
        status: "ALL",
        sortOrder: "DESC",
        search: debouncedSearch || undefined,
        startDate: dateRange.from || undefined,
        endDate: dateRange.to || undefined,
      }),
    );
  }, [dispatch, page, limit, debouncedSearch, dateRange.from, dateRange.to]);

  const records = pearlsCommission.data || [];
  const totalRecords =
    pearlsCommission.pagination?.total ?? records.length;

  return (
    <section className="salar-pearls-page">
      <div className="salar-pearls-shell">
        <header className="salar-pearls-page-header">
          <div className="salar-pearls-title-wrap">
            <span className="salar-pearls-title-icon">
              <Gem size={23} />
            </span>
            <div>
              <span className="salar-pearls-eyebrow">MY REWARDS</span>
              <h1>Salar Pearls</h1>
              <p>
                View earned Pearls, milestone details and the voucher
                purchase that generated each reward.
              </p>
            </div>
          </div>
          <div className="salar-pearls-summary">
            <span>Total Pearls</span>
            <strong>
              {(pearlsCommission.totalPearlsReferralCommission ?? 0).toLocaleString("en-IN")}
            </strong>
          </div>
        </header>

        <div className="salar-pearls-toolbar">
          <select
            value={limit}
            onChange={(e) => {
              setLimit(Number(e.target.value));
              setPage(1);
            }}
            aria-label="Entries per page"
          >
            {[10, 25, 50, 75, 100].map((val) => (
              <option value={val} key={val}>
                {val} Entries
              </option>
            ))}
          </select>

          <div className="salar-pearls-date-picker">
            <DateRangePicker
              value={dateRange}
              onChange={(v) => {
                setDateRange(v);
                setPage(1);
              }}
            />
          </div>

          <span className="salar-pearls-record-count">
            {totalRecords} records
          </span>

          <div className="salar-pearls-search">
            <Search size={17} />
            <input
              type="search"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="Search pearls..."
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch("")}
                aria-label="Clear search"
              >
                <X size={15} />
              </button>
            )}
          </div>
        </div>

        <div className="salar-pearls-desktop-list">
          {records.map((item, index) => (
            <article className="salar-pearls-desktop-card" key={index}>
              <div className="salar-pearls-meta-row">
                <div>
                  <span>Date</span>
                  <strong>{item.date}</strong>
                </div>
                <div>
                  <span>Order ID</span>
                  <strong>{item.orderId}</strong>
                </div>
                <div>
                  <span>Achieved Date</span>
                  <strong className="salar-pearls-achieved">
                    {formatEarningsDate(item.achievedDate)}
                  </strong>
                </div>
                <div className="salar-pearls-meta-status">
                  <span>Salar Pearl Status</span>
                  <span className={statusClass(item.salarPearlStatus)}>
                    {statusLabel(item.salarPearlStatus)}
                  </span>
                </div>
              </div>

              <div className="salar-pearls-record-body">
                <div className="salar-pearls-purchased-row">
                  <div>
                    <strong>
                      Purchased On {formatEarningsLongDate(item.purchasedOn)}
                    </strong>
                    <span>{item.purchaseTitle || "Salar Shopping Voucher"}</span>
                  </div>
                  <Link
                    className="salar-pearls-view-details"
                    to={
                      item.recordId
                        ? `/SalarCoinsDetails/${item.recordId}`
                        : "/SalarCoins"
                    }
                  >
                    <Eye size={16} />
                    View Details
                  </Link>
                </div>

                <div className="salar-pearls-product-row">
                  <img
                    src={item.productImage || "../../assets/img/women/women-4.png"}
                    alt=""
                    className="salar-pearls-product-image"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src =
                        "../../assets/img/women/women-4.png";
                    }}
                  />
                  <div className="salar-pearls-product-copy">
                    <span className="salar-pearls-section-label">
                      PEARLS PURCHASE
                    </span>
                    <h2>{item.productName}</h2>
                    <p>{item.milestoneName}</p>
                    <div className="salar-pearls-actions">
                      <Link
                        className="salar-pearls-secondary-action"
                        to={
                          item.recordId
                            ? `/SalarCoinsDetails/${item.recordId}`
                            : "/SalarCoins"
                        }
                      >
                        <Eye size={15} />
                        View your item
                      </Link>
                    </div>
                  </div>
                </div>

                <div className="salar-pearls-info-grid">
                  <div>
                    <span>Salar Shopping Voucher</span>
                    <strong>
                      {formatEarningsCurrency(item.salarShoppingVoucher)}
                    </strong>
                  </div>
                  <div>i
                    <span>Milestone Name</span>
                    <strong>{item.milestoneName}</strong>
                  </div>
                  <div className="salar-pearls-balance-box">
                    <span>Salar Pearls</span>
                    <strong>
                      {(item.salarPearls ?? 0).toLocaleString("en-IN")}
                    </strong>
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>

        <div className="salar-pearls-mobile-list">
          {records.map((item, index) => (
            <Link
              className="salar-pearls-mobile-card"
              key={index}
              to={
                item.recordId
                  ? `/SalarCoinsDetails/${item.recordId}`
                  : "/SalarCoins"
              }
            >
              <span className="salar-pearls-mobile-index">{item.sNo}</span>
              <img
                src={item.productImage || "../../assets/img/mobiles/mobiles-4.png"}
                alt=""
                onError={(e) => {
                  (e.target as HTMLImageElement).src =
                    "../../assets/images/logos/salarlogo.png";
                }}
              />
              <span className="salar-pearls-mobile-copy">
                <span className="salar-pearls-mobile-topline">
                  <span className={statusClass(item.salarPearlStatus)}>
                    {statusLabel(item.salarPearlStatus)}
                  </span>
                  <span className="salar-pearls-mobile-date">
                    <CalendarDays size={14} />
                    {item.date}
                  </span>
                </span>
                <strong className="salar-pearls-mobile-title">
                  {item.productName}
                </strong>
                <span className="salar-pearls-mobile-description">
                  {item.milestoneName}
                </span>
                <span className="salar-pearls-mobile-bottomline">
                  <strong>
                    {(item.salarPearls ?? 0).toLocaleString("en-IN")} Pearls
                  </strong>
                  <span>{item.orderId}</span>
                </span>
              </span>
              <ChevronRight
                size={18}
                className="salar-pearls-mobile-chevron"
              />
            </Link>
          ))}
        </div>

        {records.length === 0 && (
          <div className="salar-pearls-empty">
            <Gem size={30} />
            <strong>No pearls found</strong>
            <span>Change the search or selected date range.</span>
          </div>
        )}

        <Pagenation
          currentPage={page}
          totalPages={pearlsCommission.pagination?.totalPages || 0}
          onPageChange={setPage}
        />
      </div>
    </section>
  );
};

export default SalarCoins;
